import { MAIN_CHAT_MODE, type RoutineLibraryItem } from '@littlebird/sdk';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { ModalsManager } from '@/components/modals/modalsManager';
import { performAttributedSend } from '@/features/chat/chat-send';
import { useRoutinesLimitState } from '@/features/subscription/hooks/use-routines-limit-state';
import { trackEvent } from '@/utils/analytics';
import { NEW_CHAT_ID } from '@/utils/constants';
import { navigate } from '@/utils/navigation-service';

import { useExpandedRoutineLibraryQuery } from '../routine-library-first.query';
import { ROUTINE_CHAT_PROMPT } from '../routine-setup-prompt';
import { CreateRoutineModal } from '../routines-create';

export function useRoutineLibraryFirst(onClose: () => void) {
  const library = useExpandedRoutineLibraryQuery();
  const { hasReachedLimit } = useRoutinesLimitState();
  const [prompt, setPrompt] = useState(ROUTINE_CHAT_PROMPT);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitting = useRef(false);
  const tabs = (library.data?.tabs ?? []).filter((tab) => tab.count > 0);
  const effectiveTab = tabs.some((tab) => tab.slug === activeTab)
    ? activeTab
    : 'all';
  const query = search.trim().toLocaleLowerCase();
  const items = (library.data?.items ?? []).filter(
    (item) =>
      (effectiveTab === 'all' || item.tags.includes(effectiveTab)) &&
      [item.title, item.description, ...(item.aliases ?? [])].some((text) =>
        text.toLocaleLowerCase().includes(query)
      )
  );

  const startChat = async (request: string) => {
    if (!request.trim() || submitting.current || hasReachedLimit) return;
    submitting.current = true;
    setIsSubmitting(true);
    try {
      const dispatch = await performAttributedSend({
        threadId: NEW_CHAT_ID,
        mode: MAIN_CHAT_MODE,
        content: request.trim(),
        requestResponseAudio: false,
      });
      if (dispatch.createdThreadId) {
        await navigate({
          to: '/chats/$chatId',
          params: { chatId: dispatch.createdThreadId },
        });
      }
      onClose();
    } catch {
      toast.error('Could not open routine setup. Please try again.');
    } finally {
      submitting.current = false;
      setIsSubmitting(false);
    }
  };

  const selectTemplate = (item: RoutineLibraryItem) => {
    if (hasReachedLimit || submitting.current) return;
    trackEvent('ROUTINE_LIBRARY_EVENT', {
      actionName: 'library_template_clicked',
      templateId: item.id,
      templateTitle: item.title,
      templateSource: item.source ?? 'system',
      surface: 'library_first',
    });
    if (item.requires_setup_chat) {
      void startChat(
        [
          `Help me set up the ${item.title} routine using this template.`,
          'Check available sources and collect the missing settings before creating it. Confirm the actual schedule, timezone, destinations and allowed actions with me. Do not enable it or run it just from this template selection.',
          `Intended timing: ${item.schedule_label ?? ''}`,
          `Required settings: ${item.setup_details ?? ''}`,
          `Template instructions:\n${item.prompt}`,
        ].join('\n\n')
      );
      return;
    }
    onClose();
    ModalsManager.show(CreateRoutineModal, { libraryItem: item });
  };

  const openManualSetup = () => {
    if (hasReachedLimit || submitting.current) return;
    onClose();
    ModalsManager.show(CreateRoutineModal, { isCustom: true });
  };

  return {
    library,
    prompt,
    setPrompt,
    activeTab: effectiveTab,
    setActiveTab,
    search,
    setSearch,
    tabs,
    items,
    isSubmitting,
    hasReachedLimit,
    submit: () => void startChat(prompt),
    selectTemplate,
    openManualSetup,
  };
}
